import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./editServiceModal.css";
import { FiClock, FiTag, FiTool, FiX } from "react-icons/fi";
import { IoChevronDownOutline, IoCheckmarkSharp } from "react-icons/io5";
import { GoTrash } from "react-icons/go";
import { motion, AnimatePresence } from "framer-motion";
import DeleteConfirmModal from "./DeleteConfirmModal";
import toast from "react-hot-toast";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getInitialDurationState = (service) => {
  const serviceDuration = toNumber(service?.duration);

  if ([15, 30, 60, 120].includes(serviceDuration)) {
    return {
      duration: serviceDuration,
      customDuration: "",
    };
  }

  return {
    duration: "Other",
    customDuration: serviceDuration ?? "",
  };
};

const getInitialServiceCountState = (service) => {
  const rawServiceCount = service?.service_count;
  const normalizedString = String(rawServiceCount ?? "")
    .trim()
    .toLowerCase();

  if (normalizedString === "unlimited") {
    return {
      numServices: "Unlimited",
      customNum: "",
    };
  }

  const serviceCount = toNumber(rawServiceCount);

  if (serviceCount !== null) {
    if ([10, 20].includes(serviceCount)) {
      return {
        numServices: String(serviceCount),
        customNum: "",
      };
    }
    return {
      numServices: "Other",
      customNum: String(serviceCount),
    };
  }

  return {
    numServices: "Unlimited",
    customNum: "",
  };
};

export default function EditServiceModal({
  onClose,
  service,
  onServiceUpdated,
  onServiceDeleted,
}) {
  const { t } = useTranslation();
  const modalRef = useRef(null);

  const initialDurationState = getInitialDurationState(service);
  const initialServiceCountState = getInitialServiceCountState(service);

  const [name, setName] = useState(service?.name || "");
  const [price, setPrice] = useState(
    service?.price !== undefined && service?.price !== null
      ? String(service.price)
      : "",
  );
  const [description, setDescription] = useState(service?.description || "");

  const [duration, setDuration] = useState(initialDurationState.duration);
  const [customDuration, setCustomDuration] = useState(
    initialDurationState.customDuration,
  );

  const [numServices, setNumServices] = useState(
    initialServiceCountState.numServices,
  );
  const [customNum, setCustomNum] = useState(
    initialServiceCountState.customNum,
  );

  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const durationOptions = [
    { label: t("serviceModal.optMinutes", { count: 15 }), value: 15 },
    { label: t("serviceModal.optMinutes", { count: 30 }), value: 30 },
    { label: t("serviceModal.optHour"), value: 60 },
    { label: t("serviceModal.optHours", { count: 2 }), value: 120 },
    { label: t("serviceModal.optOther"), value: "Other" },
  ];

  const numOptions = [
    { label: "10", value: "10" },
    { label: "20", value: "20" },
    { label: t("serviceModal.optUnlimited"), value: "Unlimited" },
    { label: t("serviceModal.optOther"), value: "Other" },
  ];

  const toggleDropdown = (type) => {
    if (loading) return;
    setOpenDropdown((prev) => (prev === type ? null : type));
  };

  const handleSelect = (type, value) => {
    if (loading) return;

    if (type === "duration") {
      setDuration(value);
      if (value !== "Other") setCustomDuration("");
    }

    if (type === "services") {
      setNumServices(value);
      if (value !== "Other") setCustomNum("");
    }

    setOpenDropdown(null);
  };

  const handleSave = async () => {
    const finalDuration =
      duration === "Other"
        ? parseInt(customDuration, 10) || 30
        : parseInt(duration, 10);

    const parsedCustom = parseInt(customNum, 10);
    const finalServiceCount =
      numServices === "Other"
        ? customNum !== "" && !isNaN(parsedCustom)
          ? parsedCustom
          : 0
        : numServices === "Unlimited" || !numServices
          ? "Unlimited"
          : parseInt(numServices, 10);

    if (!name.trim() || !price || !description.trim()) {
      toast.error(t("serviceModal.toastFillFields"));
      return;
    }

    if (!finalDuration || finalDuration <= 0) {
      toast.error(t("serviceModal.toastValidDuration"));
      return;
    }

    if (
      numServices === "Other" &&
      (customNum === "" || isNaN(parsedCustom) || parsedCustom < 0)
    ) {
      toast.error(t("serviceModal.toastValidNumServices"));
      return;
    }

    if (Number(price) <= 0) {
      toast.error(t("serviceModal.toastValidPrice"));
      return;
    }

    setLoading(true);

    try {
      const updatedService = {
        id: service.id,
        name: name.trim(),
        description: description.trim(),
        duration: finalDuration,
        price: parseFloat(price),
        service_count: finalServiceCount,
        currency: "USD",
        time_unit: "minutes",
        on_off: typeof service?.on_off === "boolean" ? service.on_off : false,
      };

      toast.success(t("serviceModal.toastSuccessUpdate"));

      if (onServiceUpdated) {
        onServiceUpdated(updatedService);
      }

      onClose();
    } catch (error) {
      toast.error(
        t("serviceModal.errorGenericUpdate", { message: error.message }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteModal(false);
    setLoading(true);

    try {
      toast.success(t("serviceModal.toastSuccessDelete"));

      if (onServiceDeleted) {
        onServiceDeleted(service.id);
      }

      onClose();
    } catch (err) {
      toast.error(
        t("serviceModal.errorGenericDelete", { message: err.message }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        className="create-service-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        <motion.div
          ref={modalRef}
          className="create-service-modal"
          initial={{ opacity: 0, scale: 0.8, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 30 }}
          transition={{ type: "spring", stiffness: 150, damping: 18 }}
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="create-service-header edit-service-header">
            <h2 className="edit-modal-title">
              {t("serviceModal.modalTitleEdit")}
            </h2>
            <button
              type="button"
              className="edit-service-close-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
            >
              <FiX size={24} className="edit-service-close-icon" />
            </button>
          </div>

          <div className="create-service-form">
            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>{t("serviceModal.labelName")}</label>
                <input
                  type="text"
                  placeholder={t("serviceModal.placeholderName")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>{t("serviceModal.labelDuration")}</label>
                <div
                  className="create-service-dropdown-field"
                  onClick={() => toggleDropdown("duration")}
                >
                  <FiClock className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {duration === "Other"
                      ? `${customDuration || t("serviceModal.optCustom")} ${t("serviceModal.unitMin")}`
                      : duration === 15 ||
                          duration === 30 ||
                          duration === 60 ||
                          duration === 120
                        ? durationOptions.find((o) => o.value === duration)
                            ?.label ||
                          `${duration} ${t("serviceModal.unitMin")}`
                        : `${duration} ${t("serviceModal.unitMin")}`}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "duration" && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {durationOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("duration", opt.value)}
                        >
                          <span>{opt.label}</span>
                          {duration === opt.value ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {duration === "Other" && (
                  <input
                    type="number"
                    className="create-service-custom-input"
                    placeholder={t("serviceModal.placeholderCustomDuration")}
                    value={customDuration}
                    onChange={(e) => setCustomDuration(e.target.value)}
                    min="1"
                  />
                )}
              </div>
            </div>

            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>{t("serviceModal.labelPrice")}</label>
                <div className="create-service-input-icon-field">
                  <FiTag className="create-service-icon" />
                  <input
                    type="number"
                    placeholder={t("serviceModal.placeholderPrice")}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="create-service-number-input"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>{t("serviceModal.labelNumServices")}</label>
                <div
                  className="create-service-dropdown-field"
                  onClick={() => toggleDropdown("services")}
                >
                  <FiTool className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {numServices === "Other"
                      ? customNum !== "" &&
                        customNum !== null &&
                        customNum !== undefined
                        ? customNum
                        : t("serviceModal.optCustom")
                      : numServices === "Unlimited"
                        ? t("serviceModal.optUnlimited")
                        : numServices}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "services" && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {numOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("services", opt.value)}
                        >
                          <span>{opt.label}</span>
                          {numServices === opt.value ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {numServices === "Other" && (
                  <input
                    type="number"
                    className="create-service-custom-input"
                    placeholder={t("serviceModal.placeholderCustomNum")}
                    value={customNum}
                    onChange={(e) => setCustomNum(e.target.value)}
                    min="0"
                  />
                )}
              </div>
            </div>

            <div className="create-service-input-group create-service-textarea">
              <label>{t("serviceModal.labelDescription")}</label>
              <textarea
                placeholder={t("serviceModal.placeholderDescription")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="create-service-footer edit-service-footer">
            <button
              className="create-service-btn"
              onClick={handleSave}
              disabled={loading}
            >
              {loading
                ? t("serviceModal.btnSaving")
                : t("serviceModal.btnSave")}
            </button>
            <button
              className="delete-service-btn"
              onClick={() => setShowDeleteModal(true)}
              disabled={loading}
            >
              <GoTrash /> {t("serviceModal.btnDelete")}
            </button>
          </div>

          {showDeleteModal && (
            <DeleteConfirmModal
              onCancel={() => setShowDeleteModal(false)}
              onDelete={handleDelete}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
